export const RENDER_STATE_VERSION = 1;

export const DEFAULT_RENDER_DIAGNOSTICS = Object.freeze({
  showGrid: true,
  showTraversal: false,
  showLabels: false,
  showBounds: false,
});

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalize(value[key])]),
  );
}

function checksumPayload(payload) {
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeRenderStateChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    throw new TypeError('snapshot must be an object');
  }
  const payload = clone(snapshot);
  delete payload.checksum;
  return checksumPayload(payload);
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${name} must be a plain object`);
  }
  return value;
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function assertFinite(value, name) {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite`);
  return value;
}

function assertFiniteNonNegative(value, name) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative finite number`);
  }
  return value;
}

function assertPositiveFinite(value, name) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function normalizeTick(value) {
  return value === null || value === undefined
    ? null
    : assertNonNegativeInteger(value, 'tick');
}

function normalizePosition(value = {}, name = 'position', fallback = { x: 0, y: 0, z: 0 }) {
  assertPlainObject(value, name);
  return {
    x: assertFinite(value.x ?? fallback.x, `${name}.x`),
    y: assertFinite(value.y ?? fallback.y, `${name}.y`),
    z: assertFinite(value.z ?? fallback.z, `${name}.z`),
  };
}

function normalizeEnvironment(value = {}) {
  assertPlainObject(value, 'environment');
  const activeScenario = value.activeScenario ?? null;
  if (activeScenario !== null) assertNonEmptyString(activeScenario, 'environment.activeScenario');
  return {
    activeScenario,
    timeOfDay: assertNonEmptyString(value.timeOfDay ?? 'day', 'environment.timeOfDay'),
    weather: assertNonEmptyString(value.weather ?? 'clear', 'environment.weather'),
  };
}

function normalizeDiagnostics(value = {}) {
  assertPlainObject(value, 'diagnostics');
  return {
    showGrid: value.showGrid === undefined
      ? DEFAULT_RENDER_DIAGNOSTICS.showGrid
      : Boolean(value.showGrid),
    showTraversal: value.showTraversal === undefined
      ? DEFAULT_RENDER_DIAGNOSTICS.showTraversal
      : Boolean(value.showTraversal),
    showLabels: value.showLabels === undefined
      ? DEFAULT_RENDER_DIAGNOSTICS.showLabels
      : Boolean(value.showLabels),
    showBounds: value.showBounds === undefined
      ? DEFAULT_RENDER_DIAGNOSTICS.showBounds
      : Boolean(value.showBounds),
  };
}

function normalizeJson(value, name) {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') return assertFinite(value, name);
  if (Array.isArray(value)) {
    return value.map((entry, index) => normalizeJson(entry, `${name}[${index}]`));
  }
  assertPlainObject(value, name);
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => {
        if (value[key] === undefined) throw new TypeError(`${name}.${key} cannot be undefined`);
        return [key, normalizeJson(value[key], `${name}.${key}`)];
      }),
  );
}

function normalizeActors(traversalState) {
  if (traversalState === null || traversalState === undefined) return [];
  assertPlainObject(traversalState, 'traversalState');
  if (!Array.isArray(traversalState.actors)) {
    throw new TypeError('traversalState.actors must be an array');
  }
  const actors = traversalState.actors.map((entry, index) => {
    assertPlainObject(entry, `traversalState.actors[${index}]`);
    return {
      actorId: assertNonEmptyString(entry.actorId, `traversalState.actors[${index}].actorId`),
      actorKind: assertNonEmptyString(entry.actorKind, `traversalState.actors[${index}].actorKind`),
      position: normalizePosition(entry.position, `traversalState.actors[${index}].position`),
      mode: assertNonEmptyString(entry.mode, `traversalState.actors[${index}].mode`),
      blocked: Boolean(entry.blocked),
      inWater: Boolean(entry.inWater),
      onClimbable: Boolean(entry.onClimbable),
      hidden: Boolean(entry.hidden),
      collisionVolumeIds: [...(entry.collisionVolumeIds ?? [])].sort(),
      contactVolumeIds: [...(entry.contactVolumeIds ?? [])].sort(),
      hazardVolumeIds: [...(entry.hazardVolumeIds ?? [])].sort(),
    };
  }).sort((left, right) => left.actorId.localeCompare(right.actorId));
  if (new Set(actors.map((actor) => actor.actorId)).size !== actors.length) {
    throw new RangeError('traversalState contains duplicate actor IDs');
  }
  return actors;
}

function normalizeVolumes(traversalState) {
  if (traversalState === null || traversalState === undefined) return [];
  assertPlainObject(traversalState, 'traversalState');
  if (!Array.isArray(traversalState.volumes)) {
    throw new TypeError('traversalState.volumes must be an array');
  }
  const volumes = traversalState.volumes.map((entry, index) => {
    assertPlainObject(entry, `traversalState.volumes[${index}]`);
    assertPlainObject(entry.bounds, `traversalState.volumes[${index}].bounds`);
    const min = normalizePosition(entry.bounds.min, `traversalState.volumes[${index}].bounds.min`);
    const max = normalizePosition(entry.bounds.max, `traversalState.volumes[${index}].bounds.max`);
    for (const axis of ['x', 'y', 'z']) {
      if (min[axis] >= max[axis]) {
        throw new RangeError(`traversal volume ${entry.id} has invalid ${axis} bounds`);
      }
    }
    return {
      id: assertNonEmptyString(entry.id, `traversalState.volumes[${index}].id`),
      kind: assertNonEmptyString(entry.kind, `traversalState.volumes[${index}].kind`),
      bounds: { min, max },
      metadata: normalizeJson(entry.metadata ?? {}, `traversalState.volumes[${index}].metadata`),
    };
  }).sort((left, right) => left.id.localeCompare(right.id));
  if (new Set(volumes.map((volume) => volume.id)).size !== volumes.length) {
    throw new RangeError('traversalState contains duplicate volume IDs');
  }
  return volumes;
}

function normalizeSnake(value, actors) {
  if (value === null || value === undefined) return null;
  assertPlainObject(value, 'snakeState');
  const entityId = assertNonEmptyString(value.entityId, 'snakeState.entityId');
  if (!Array.isArray(value.bodySegments) || value.bodySegments.length < 2) {
    throw new TypeError('snakeState.bodySegments must contain at least two segments');
  }
  const bodySegments = value.bodySegments.map((segment, index) => {
    assertPlainObject(segment, `snakeState.bodySegments[${index}]`);
    return {
      index: Number.isInteger(segment.index) ? segment.index : index,
      ...normalizePosition(segment, `snakeState.bodySegments[${index}]`),
    };
  }).sort((left, right) => left.index - right.index);
  const traversal = actors.find((actor) => actor.actorId === entityId) ?? null;
  const vitals = assertPlainObject(value.vitals, 'snakeState.vitals');
  const growth = assertPlainObject(value.growth, 'snakeState.growth');
  return {
    entityId,
    species: assertNonEmptyString(value.species, 'snakeState.species'),
    lifecycle: assertNonEmptyString(value.lifecycle, 'snakeState.lifecycle'),
    position: normalizePosition(value.position, 'snakeState.position'),
    heading: assertFinite(value.heading, 'snakeState.heading'),
    speed: assertFinite(value.speed, 'snakeState.speed'),
    length: assertPositiveFinite(value.length, 'snakeState.length'),
    mass: assertPositiveFinite(value.mass, 'snakeState.mass'),
    bodySegments,
    vitals: {
      health: assertFiniteNonNegative(vitals.health, 'snakeState.vitals.health'),
      hunger: assertFiniteNonNegative(vitals.hunger, 'snakeState.vitals.hunger'),
      stamina: assertFiniteNonNegative(vitals.stamina, 'snakeState.vitals.stamina'),
      temperature: assertFinite(vitals.temperature, 'snakeState.vitals.temperature'),
    },
    growth: {
      stage: assertNonEmptyString(growth.stage, 'snakeState.growth.stage'),
      points: assertFiniteNonNegative(growth.points, 'snakeState.growth.points'),
    },
    traversal: traversal === null ? {
      mode: 'ground',
      blocked: false,
      hidden: false,
      inWater: false,
      onClimbable: false,
      hazardVolumeIds: [],
    } : {
      mode: traversal.mode,
      blocked: traversal.blocked,
      hidden: traversal.hidden,
      inWater: traversal.inWater,
      onClimbable: traversal.onClimbable,
      hazardVolumeIds: traversal.hazardVolumeIds,
    },
  };
}

function normalizeWildlife(value) {
  if (value === null || value === undefined) return [];
  assertPlainObject(value, 'wildlifeState');
  if (!Array.isArray(value.wildlife)) throw new TypeError('wildlifeState.wildlife must be an array');
  const wildlife = value.wildlife.map((entry, index) => {
    assertPlainObject(entry, `wildlifeState.wildlife[${index}]`);
    const maxHealth = assertPositiveFinite(entry.maxHealth, `wildlifeState.wildlife[${index}].maxHealth`);
    const health = assertFiniteNonNegative(entry.health, `wildlifeState.wildlife[${index}].health`);
    if (health > maxHealth) throw new RangeError('wildlife health cannot exceed maxHealth');
    return {
      entityId: assertNonEmptyString(entry.entityId, `wildlifeState.wildlife[${index}].entityId`),
      speciesId: assertNonEmptyString(entry.speciesId, `wildlifeState.wildlife[${index}].speciesId`),
      role: assertNonEmptyString(entry.role, `wildlifeState.wildlife[${index}].role`),
      lifecycle: assertNonEmptyString(entry.lifecycle, `wildlifeState.wildlife[${index}].lifecycle`),
      position: normalizePosition(entry.position, `wildlifeState.wildlife[${index}].position`),
      heading: assertFinite(entry.heading, `wildlifeState.wildlife[${index}].heading`),
      speed: assertFiniteNonNegative(entry.speed, `wildlifeState.wildlife[${index}].speed`),
      behavior: assertNonEmptyString(entry.behavior, `wildlifeState.wildlife[${index}].behavior`),
      targetEntityId: entry.targetEntityId ?? null,
      health,
      maxHealth,
      healthRatio: health / maxHealth,
    };
  }).sort((left, right) => left.entityId.localeCompare(right.entityId));
  if (new Set(wildlife.map((entry) => entry.entityId)).size !== wildlife.length) {
    throw new RangeError('wildlifeState contains duplicate entity IDs');
  }
  return wildlife;
}

function normalizeStory(value) {
  if (value === null || value === undefined) {
    return {
      mode: 'inactive',
      status: 'idle',
      activeMissionId: null,
      completedMissionCount: 0,
    };
  }
  assertPlainObject(value, 'storyState');
  const missions = Array.isArray(value.missions) ? value.missions : [];
  return {
    mode: assertNonEmptyString(value.mode ?? 'inactive', 'storyState.mode'),
    status: assertNonEmptyString(value.status ?? 'idle', 'storyState.status'),
    activeMissionId: value.activeMissionId ?? null,
    completedMissionCount: missions.filter((mission) => mission?.status === 'completed').length,
  };
}

function normalizeCamera(value, snake) {
  assertPlainObject(value, 'camera');
  const target = normalizePosition(value.target ?? snake?.position ?? {}, 'camera.target');
  const heading = assertFinite(value.heading ?? snake?.heading ?? 0, 'camera.heading');
  const zoom = assertPositiveFinite(value.zoom ?? 1, 'camera.zoom');
  if (zoom < 0.25 || zoom > 4) throw new RangeError('camera.zoom must be between 0.25 and 4');
  const viewRadius = assertPositiveFinite(value.viewRadius ?? 8, 'camera.viewRadius');
  if (viewRadius < 2 || viewRadius > 100) {
    throw new RangeError('camera.viewRadius must be between 2 and 100');
  }
  return { target, heading, zoom, viewRadius };
}

export function projectRenderState({
  tick = null,
  simulationTime = 0,
  environment = {},
  snakeState = null,
  wildlifeState = null,
  traversalState = null,
  storyState = null,
  camera = {},
  diagnostics = {},
} = {}) {
  const actors = normalizeActors(traversalState);
  const snake = normalizeSnake(snakeState, actors);
  const volumes = normalizeVolumes(traversalState);
  const payload = {
    version: RENDER_STATE_VERSION,
    tick: normalizeTick(tick),
    simulationTime: assertFiniteNonNegative(simulationTime, 'simulationTime'),
    environment: normalizeEnvironment(environment),
    camera: normalizeCamera(camera, snake),
    snake,
    wildlife: normalizeWildlife(wildlifeState),
    traversal: {
      volumes,
      actors,
    },
    story: normalizeStory(storyState),
    diagnostics: normalizeDiagnostics(diagnostics),
    sourceChecksums: {
      snake: snakeState?.checksum ?? null,
      wildlife: wildlifeState?.checksum ?? null,
      traversal: traversalState?.checksum ?? null,
      story: storyState?.checksum ?? null,
    },
  };
  return deepFreeze({
    ...payload,
    checksum: computeRenderStateChecksum(payload),
  });
}
